/**
 * Sentry 错误监控 — 朋际 v5.1
 * 
 * 使用 Sentry Browser SDK 进行前端错误监控。
 * 生产环境自动上报，开发环境仅 console 输出。
 */

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || '';

let Sentry = null;

/**
 * 初始化 Sentry
 * 在 index.js 中调用
 */
export async function initSentry() {
  // 仅在生产环境且有 DSN 时初始化
  if (process.env.NODE_ENV !== 'production' || !SENTRY_DSN) {
    console.log('[Sentry] 跳过初始化 (dev mode 或无 DSN)');
    return;
  }

  try {
    // 动态导入，避免增加开发环境包体积
    const SentryModule = await import('@sentry/react');
    Sentry = SentryModule;

    Sentry.init({
      dsn: SENTRY_DSN,
      integrations: [
        new Sentry.BrowserTracing({
          tracePropagationTargets: ['localhost', /^https?:\/\/.*pengji\.app/],
        }),
        new Sentry.Replay({
          maskAllText: true,        // 隐私保护：遮罩所有文本
          blockAllMedia: true,      // 不录制媒体
          maskAllInputs: true,      // 遮罩输入框
        }),
      ],
      // 性能监控采样率
      tracesSampleRate: 0.1,       // 10% 采样
      // Session Replay 采样率
      replaysSessionSampleRate: 0.01, // 1% 采样（仅开发/测试用）
      replaysOnErrorSampleRate: 1.0,  // 错误时 100% 录制
      // 环境
      environment: process.env.NODE_ENV,
      // 版本号
      release: process.env.REACT_APP_VERSION || '0.1.0',
      // 不收集个人敏感信息
      beforeSend(event) {
        // 过滤掉包含敏感信息的错误
        if (event.request?.cookies) {
          delete event.request.cookies;
        }
        // 不收集 localStorage
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.filter(b => b.category !== 'localStorage');
        }
        return event;
      },
    });

    // 挂载到 window 供 ErrorBoundary 使用
    window.Sentry = Sentry;
    console.log('[Sentry] 初始化完成');
  } catch (err) {
    console.warn('[Sentry] 初始化失败:', err.message);
  }
}

/**
 * 手动上报错误
 */
export function captureError(error, context = {}) {
  if (Sentry) {
    Sentry.captureException(error, { contexts: { custom: context } });
  } else if (process.env.NODE_ENV === 'development') {
    console.error('[Sentry Mock]', error, context);
  }
}

/**
 * 手动上报消息
 */
export function captureMessage(message, level = 'info') {
  if (Sentry) {
    Sentry.captureMessage(message, level);
  } else if (process.env.NODE_ENV === 'development') {
    console.log(`[Sentry Mock ${level}]`, message);
  }
}

/**
 * 设置用户上下文
 */
export function setUserContext(user) {
  if (Sentry && user) {
    Sentry.setUser({
      id: user.id,
      username: user.nickname,
      // 不设置 phone/email 等敏感信息
    });
  }
}

/**
 * 清除用户上下文（退出登录时调用）
 */
export function clearUserContext() {
  if (Sentry) {
    Sentry.setUser(null);
  }
}

export default {
  init: initSentry,
  captureError,
  captureMessage,
  setUserContext,
  clearUserContext,
};
